import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, ChevronRight as ArrowRight } from 'lucide-react';
import api from '../../api/axios';
import DataGrid from '../../components/DataGrid';
import ConfirmDialog from '../../components/ConfirmDialog';
import { PLAN_STYLE, STATUS_STYLE, type SocietyPlan, type SocietyStatus } from '../../types/societyAdmin';

/* ── Types ───────────────────────────────────────────────────────────────── */

interface AdminSociety {
  id: string;
  name: string;
  code?: string;
  city?: string;
  plan: SocietyPlan;
  status: SocietyStatus;
  unitCount: number;
  adminEmail?: string;
  createdAt: string;
}

interface Paged<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
}

type Envelope<T> = { success?: boolean; data?: T } | T;
function unwrap<T>(raw: Envelope<T>): T {
  return (raw as { data?: T })?.data ?? (raw as T);
}

/* ── Badge helpers ───────────────────────────────────────────────────────── */

const Badge: React.FC<{ label: string; color: string; bg: string }> = ({ label, color, bg }) => (
  <span style={{ padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600, color, background: bg }}>
    {label}
  </span>
);

const fmt = (iso: string) =>
  new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

/* ── SocietyManagementPage ───────────────────────────────────────────────── */

const PAGE_SIZE = 15;

const SocietyManagementPage: React.FC = () => {
  const navigate = useNavigate();

  const [societies,     setSocieties]     = useState<AdminSociety[]>([]);
  const [loading,       setLoading]       = useState(true);
  const [page,          setPage]          = useState(0);
  const [totalPages,    setTotalPages]    = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [statusFilter,  setStatusFilter]  = useState('');
  const [planFilter,    setPlanFilter]    = useState('');

  type ActionKind = 'suspend' | 'reactivate';
  const [confirmTarget, setConfirmTarget] = useState<{ id: string; name: string; kind: ActionKind } | null>(null);
  const [actioning,     setActioning]     = useState(false);
  const [actionError,   setActionError]   = useState('');

  const fetchSocieties = async (p: number) => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = { page: p, size: PAGE_SIZE };
      if (statusFilter) params.status = statusFilter;
      if (planFilter)   params.plan   = planFilter;
      const res  = await api.get('/api/v1/admin/societies', { params });
      const data = unwrap<Paged<AdminSociety>>(res.data);
      setSocieties(data.content ?? []);
      setTotalPages(data.totalPages ?? 0);
      setTotalElements(data.totalElements ?? 0);
    } catch { setSocieties([]); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    setPage(0);
    fetchSocieties(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, planFilter]);

  const goToPage = (p: number) => { setPage(p); fetchSocieties(p); };

  const openConfirm = (soc: AdminSociety, kind: ActionKind) => {
    setActionError('');
    setConfirmTarget({ id: soc.id, name: soc.name, kind });
  };

  const handleAction = async () => {
    if (!confirmTarget) return;
    const snapshot = [...societies];
    setActioning(true); setActionError('');
    setSocieties(s =>
      s.map(x =>
        x.id === confirmTarget.id
          ? { ...x, status: confirmTarget.kind === 'suspend' ? 'SUSPENDED' : 'ACTIVE' }
          : x,
      ),
    );
    try {
      await api.patch(`/api/v1/admin/societies/${confirmTarget.id}/${confirmTarget.kind}`);
      setConfirmTarget(null);
    } catch {
      setSocieties(snapshot);
      setActionError('Action failed. Please try again.');
    } finally { setActioning(false); }
  };

  /* ── Column definitions ── */

  const columns = [
    {
      key: 'name', header: 'Society',
      cellStyle: { fontWeight: 600 },
      render: (soc: AdminSociety) => (
        <button
          onClick={() => navigate(`/admin/societies/${soc.id}`)}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#1A6CF6', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'var(--font-body)' }}
        >
          {soc.name} <ArrowRight size={12} />
        </button>
      ),
    },
    {
      key: 'code', header: 'Code',
      cellStyle: { color: 'var(--nx-text-secondary)', fontFamily: 'monospace', fontSize: 12 },
      render: (soc: AdminSociety) => soc.code ?? '—',
    },
    {
      key: 'societyId', header: 'Society ID',
      cellStyle: { color: 'var(--nx-text-muted)', fontFamily: 'monospace', fontSize: 11 },
      render: (soc: AdminSociety) => <span title={soc.id}>{soc.id.slice(0, 8)}…</span>,
    },
    {
      key: 'city', header: 'City',
      cellStyle: { color: 'var(--nx-text-secondary)' },
      render: (soc: AdminSociety) => soc.city ?? '—',
    },
    {
      key: 'plan', header: 'Plan',
      render: (soc: AdminSociety) => {
        const planStyle = PLAN_STYLE[soc.plan] ?? { color: '#64748B', bg: 'rgba(100,116,139,0.1)', label: soc.plan ?? 'Unknown' };
        return <Badge label={planStyle.label} color={planStyle.color} bg={planStyle.bg} />;
      },
    },
    {
      key: 'status', header: 'Status',
      render: (soc: AdminSociety) => {
        const statusStyle = STATUS_STYLE[soc.status] ?? { color: '#64748B', bg: 'rgba(100,116,139,0.1)', label: soc.status ?? 'Unknown' };
        return <Badge {...statusStyle} />;
      },
    },
    {
      key: 'units', header: 'Units',
      cellStyle: { color: 'var(--nx-text-secondary)', textAlign: 'right' as const },
      render: (soc: AdminSociety) => (soc.unitCount ?? 0).toLocaleString(),
    },
    {
      key: 'admin', header: 'Admin',
      cellStyle: { color: 'var(--nx-text-secondary)', fontSize: 12 },
      render: (soc: AdminSociety) => soc.adminEmail ?? '—',
    },
    {
      key: 'created', header: 'Created',
      cellStyle: { color: 'var(--nx-text-secondary)', whiteSpace: 'nowrap' as const },
      render: (soc: AdminSociety) => soc.createdAt ? fmt(soc.createdAt) : '—',
    },
    {
      key: 'actions', header: 'Actions',
      render: (soc: AdminSociety) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => navigate(`/admin/societies/${soc.id}`)}
            style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid var(--nx-border)', background: '#fff', color: 'var(--nx-text-secondary)', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--font-body)' }}
          >
            View
          </button>
          {soc.status === 'ACTIVE' ? (
            <button
              onClick={() => openConfirm(soc, 'suspend')}
              style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.06)', color: '#DC2626', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--font-body)' }}
            >
              Suspend
            </button>
          ) : soc.status === 'SUSPENDED' ? (
            <button
              onClick={() => openConfirm(soc, 'reactivate')}
              style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid rgba(16,185,129,0.3)', background: 'rgba(16,185,129,0.06)', color: '#059669', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--font-body)' }}
            >
              Reactivate
            </button>
          ) : null}
        </div>
      ),
    },
  ];

  /* ── Filter select style ── */
  const selStyle: React.CSSProperties = {
    padding: '8px 14px', borderRadius: 8, border: '1px solid var(--nx-border)',
    background: '#fff', fontSize: 13, color: 'var(--nx-text-primary)',
    outline: 'none', cursor: 'pointer', fontFamily: 'var(--font-body)', minWidth: 130,
  };

  return (
    <div style={{ background: '#f8fafc', minHeight: '100%', padding: 32, fontFamily: 'var(--font-body)' }}>

      {/* ── Page header ── */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 800, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em', marginBottom: 4 }}>
          Societies
        </h1>
        <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>
          Manage all tenant societies — view details, suspend, or reactivate accounts.
        </p>
      </div>

      {/* ── Filter bar ── */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20 }}>
        <select style={selStyle} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="SUSPENDED">Suspended</option>
        </select>
        <select style={selStyle} value={planFilter} onChange={e => setPlanFilter(e.target.value)}>
          <option value="">All plans</option>
          <option value="FREE">Free</option>
          <option value="STARTER">Starter</option>
          <option value="PRO">Pro</option>
        </select>
        <button
          onClick={() => fetchSocieties(page)}
          className="nx-btn nx-btn-outline nx-btn-sm"
          style={{ marginLeft: 'auto', gap: 6 }}
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* ── Table card ── */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid var(--nx-border)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--nx-border)', display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <span style={{ fontFamily: 'Syne, sans-serif', fontSize: 15, fontWeight: 700, color: 'var(--nx-text-primary)' }}>
            All Societies
          </span>
          {totalElements > 0 && (
            <span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>{totalElements} total</span>
          )}
        </div>
        <DataGrid
          columns={columns}
          rows={societies}
          rowKey={soc => soc.id}
          loading={loading}
          emptyText="No societies found matching the current filters."
          rowStyle={soc => ({ opacity: soc.status === 'SUSPENDED' ? 0.65 : 1 })}
          page={page}
          totalPages={totalPages}
          onPageChange={goToPage}
        />
      </div>

      {/* ── Confirm dialog ── */}
      <ConfirmDialog
        open={confirmTarget !== null}
        variant={confirmTarget?.kind === 'suspend' ? 'warning' : 'success'}
        title={confirmTarget ? `${confirmTarget.kind === 'suspend' ? 'Suspend' : 'Reactivate'} society?` : ''}
        message={confirmTarget
          ? confirmTarget.kind === 'suspend'
            ? `"${confirmTarget.name}" and all its users will lose access immediately.`
            : `"${confirmTarget.name}" and its users will regain access immediately.`
          : ''
        }
        confirmLabel={confirmTarget?.kind === 'suspend' ? 'Suspend' : 'Reactivate'}
        isLoading={actioning}
        error={actionError}
        onConfirm={handleAction}
        onCancel={() => { setConfirmTarget(null); setActionError(''); }}
      />
    </div>
  );
};

export default SocietyManagementPage;
